import { computed, defineComponent, Fragment, h, ref, shallowRef, unref, watch } from 'vue';

import { useRender } from '@components/composable';
import { noop, omit } from '@components/utils';

import { findExpandedKeys, getExpandRange, getKey } from '../utils';

import MotionTreeNode from './MotionTreeNode';
import { VirtualList } from './VirtualList';
import { useInjectKeysState, useInjectTreeContext } from './TreeContext';

import type { NTree } from '../interface';
import type { ScrollTo } from './VirtualList';
import type { CSSProperties, ExtractPropTypes, PropType } from 'vue';

const HIDDEN_STYLE = {
  width: 0,
  height: 0,
  display: 'flex',
  overflow: 'hidden',
  opacity: 0,
  border: 0,
  padding: 0,
  margin: 0
};

export const MOTION_KEY = `TREE_MOTION_${Math.random()}`;

const MotionNode: NTree.IDataNode = {
  key: MOTION_KEY
};

export const MotionEntity: NTree.IDataEntity = {
  key: MOTION_KEY,
  level: 0,
  index: 0,
  pos: '0',
  node: MotionNode,
  nodes: [MotionNode]
};

const MotionFlattenData: NTree.IFlattenNode = {
  parent: null,
  children: [],
  pos: MotionEntity.pos,
  data: MotionNode,
  title: null,
  key: MOTION_KEY,
  /** Hold empty list here since we do not use it */
  isStart: [],
  isEnd: []
};

export interface NodeListRef {
  scrollTo: ScrollTo;
  getIndentWidth: () => number;
}

export const nodeListProps = {
  motion: { type: Object as PropType<any> },
  focusable: { type: Boolean as PropType<boolean> },
  activeItem: { type: Object as PropType<NTree.IFlattenNode | null> },
  focused: { type: Boolean as PropType<boolean> },
  tabindex: { type: Number as PropType<number> },
  checkable: { type: Boolean as PropType<boolean> },
  selectable: { type: Boolean as PropType<boolean> },
  disabled: { type: Boolean as PropType<boolean> },

  // Virtual list
  height: { type: Number as PropType<number> },
  itemHeight: { type: Number as PropType<number> },
  virtual: { type: Boolean as PropType<boolean> },

  onScroll: { type: Function as PropType<NTree.TEventHandler> },
  onKeydown: { type: Function as PropType<NTree.TEventHandler> },
  onFocus: { type: Function as PropType<(e: FocusEvent) => void> },
  onBlur: { type: Function as PropType<(e: FocusEvent) => void> },
  onActiveChange: { type: Function as PropType<(key: NTree.TKey | null) => void> },
  onContextmenu: { type: Function as PropType<NTree.TEventHandler> },

  onListChangeStart: { type: Function as PropType<() => void> },
  onListChangeEnd: { type: Function as PropType<() => void> }
};

export type NodeListProps = Partial<ExtractPropTypes<typeof nodeListProps>>;

export default defineComponent({
  name: 'NodeList',
  inheritAttrs: false,
  props: nodeListProps,
  setup(props, { expose, attrs }) {
    const listRef = ref();
    const indentMeasurerRef = ref();
    const { expandedKeys, flattenNodes } = useInjectKeysState();

    const transitionData = shallowRef<NTree.IFlattenNode[]>(unref(flattenNodes));
    const transitionRange = shallowRef<NTree.IFlattenNode[]>([]);
    const motionType = ref<'show' | 'hide' | null>(null);

    function onMotionEnd() {
      transitionData.value = flattenNodes.value;
      transitionRange.value = [];
      motionType.value = null;

      props.onListChangeEnd && props.onListChangeEnd();
    }

    const context = useInjectTreeContext();

    watch([() => unref(expandedKeys).slice(), flattenNodes], ([expandedKeys, data], [prevExpandedKeys, prevData]) => {
      const diffExpanded = findExpandedKeys(prevExpandedKeys, expandedKeys);
      if (diffExpanded.key !== null) {
        const { virtual, height, itemHeight } = props;
        if (diffExpanded.add) {
          const keyIndex = prevData.findIndex(({ key }) => key === diffExpanded.key);
          const rangeNodes = getMinimumRangeTransitionRange(
            getExpandRange(prevData, data, diffExpanded.key),
            virtual!,
            height!,
            itemHeight!
          );

          const newTransitionData: NTree.IFlattenNode[] = prevData.slice();
          newTransitionData.splice(keyIndex + 1, 0, MotionFlattenData);

          transitionData.value = newTransitionData;
          transitionRange.value = rangeNodes;
          motionType.value = 'show';
        } else {
          const keyIndex = data.findIndex(({ key }) => key === diffExpanded.key);

          const rangeNodes = getMinimumRangeTransitionRange(
            getExpandRange(data, prevData, diffExpanded.key),
            virtual!,
            height!,
            itemHeight!
          );

          const newTransitionData: NTree.IFlattenNode[] = data.slice();
          newTransitionData.splice(keyIndex + 1, 0, MotionFlattenData);

          transitionData.value = newTransitionData;
          transitionRange.value = rangeNodes;
          motionType.value = 'hide';
        }
      } else if (prevData !== data) {
        transitionData.value = data;
      }
    });
    // We should clean up motion if is changed by dragging
    watch(
      () => unref(context).dragging,
      (dragging) => {
        if (!dragging) {
          onMotionEnd();
        }
      }
    );

    const mergedData = computed(() => (props.motion === undefined ? unref(transitionData) : unref(flattenNodes)));

    const onActiveChange = () => {
      props.onActiveChange && props.onActiveChange(null);
    };

    useRender(() => {
      const {
        selectable,
        checkable,
        disabled,

        motion,

        height,
        itemHeight,
        virtual,

        focusable,
        activeItem,
        focused,
        tabindex,

        onKeydown,
        onFocus,
        onBlur,

        onListChangeStart,
        onListChangeEnd,

        ...domProps
      } = { ...props, ...attrs } as NodeListProps;

      return h(Fragment, [
        focused &&
          activeItem &&
          h('span', { style: HIDDEN_STYLE, 'aria-live': 'assertive' }, [getAccessibilityPath(activeItem)]),

        h('div', [
          h('input', {
            style: HIDDEN_STYLE,
            disabled: focused === false || disabled,
            tabindex: focusable !== false ? tabindex : null,
            value: '',
            'aria-label': 'for screen reader',
            onKeydown,
            onFocus,
            onChange: noop
          })
        ]),

        h(
          'div',
          {
            'aria-hidden': 'true',
            class: 'cds-treenode',
            style: {
              position: 'absolute',
              pointerEvents: 'none',
              visibility: 'hidden',
              height: 0,
              overflow: 'hidden'
            } as CSSProperties
          },
          h(
            'div',
            { class: 'cds-treenode__indent' },
            h('div', { ref: indentMeasurerRef, class: 'cds-treenode__indent-unit' })
          )
        ),

        h(
          VirtualList,
          {
            ...omit(domProps, ['onActiveChange']),
            prefixCls: 'cds-tree-list',
            ref: listRef,
            data: unref(mergedData),
            height,
            fullHeight: false,
            virtual,
            itemKey: itemKey as any,
            itemHeight,
            onVisibleChange: (originList: any[], fullList: any[]) => {
              const originSet = new Set(originList);
              const restList = fullList.filter((item) => !originSet.has(item));

              // Motion node is not render. Skip motion
              if (restList.some((item) => itemKey(item) === MOTION_KEY)) {
                onMotionEnd();
              }
            }
          },
          {
            default: (treeNode: NTree.IFlattenNode) => {
              const {
                pos,
                data: { ...restProps },
                title,
                key,
                isStart,
                isEnd
              } = treeNode;

              const mergedKey = getKey(key, pos);

              delete restProps.key;
              delete restProps.children;

              return h(MotionTreeNode, {
                ...restProps,
                eventKey: mergedKey,
                title,
                active: !!activeItem && key === activeItem.key,
                data: treeNode.data,
                isStart,
                isEnd,
                motion,
                motionNodes: key === MOTION_KEY ? unref(transitionRange) : null,
                motionType: unref(motionType),
                onMotionStart: onListChangeStart,
                onMotionEnd,
                onMousemove: onActiveChange
              });
            }
          }
        )
      ]);
    });

    expose({
      scrollTo: (scroll: ScrollTo) => {
        unref(listRef).scrollTo(scroll);
      },
      getIndentWidth: () => unref(indentMeasurerRef).offsetWidth
    });
  }
});

/**
 * We only need get visible content items to play the animation.
 */
export function getMinimumRangeTransitionRange(
  list: NTree.IFlattenNode[],
  virtual: boolean,
  height: number,
  itemHeight: number
) {
  if (!virtual || !height) {
    return list;
  }

  return list.slice(0, Math.ceil(height / itemHeight) + 1);
}

function itemKey(item: NTree.IFlattenNode) {
  const { key, pos } = item;
  return getKey(key, pos);
}

function getAccessibilityPath(item: NTree.IFlattenNode): string {
  let path = String(item.key);
  let current = item;

  while (current.parent) {
    current = current.parent;
    path = `${current.key} > ${path}`;
  }

  return path;
}
